// client/src/pages/doctors/MoodAnalysisPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import moodAnalyticsService from '../../../services/moodAnalyticsService';
import { getMoodEmoji } from '../../../utils/moodUtils';
import '../../../styles/components/doctor/MoodAnalysisPage.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MoodAnalysisPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patientData = location.state?.patientData || {};

  const [moodHistory, setMoodHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [processedData, setProcessedData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [rerunningAnalysis, setRerunningAnalysis] = useState({});

  // Fetch patient mood data
  useEffect(() => {
    if (patientId) {
      fetchPatientMoodData();
    }
  }, [patientId, timeRange, selectedDate]);

  const fetchPatientMoodData = async () => {
    if (!patientId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const limitMap = {
        daily: 50,
        weekly: 100,
        monthly: 300
      };
      
      const response = await moodAnalyticsService.getUserMoodHistory(
        patientId,
        { 
          limit: limitMap[timeRange] || 200,
          page: 1, 
          includeSentiment: true 
        }
      );
      
      if (response.success) {
        console.log('Fetched mood data:', response.data.length, 'entries');
        setMoodHistory(response.data || []);
        setClinicalSummary(response.clinicalSummary || null);
        processDataByTimeRange(response.data || [], timeRange);
        
        // Filter recent entries (last 6 hours)
        const sixHoursAgo = new Date();
        sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
        const recent = (response.data || []).filter(entry => 
          new Date(entry.timestamp) >= sixHoursAgo
        );
        setRecentEntries(recent);
      } else {
        setError('Failed to load mood data');
      }
    } catch (err) {
      console.error('Error fetching mood data:', err);
      setError('Error loading patient mood data');
    } finally {
      setIsLoading(false);
    }
  };

  const processDataByTimeRange = (data, range) => {
    console.log('Processing data for range:', range, 'with', data.length, 'entries');
    
    if (!data || data.length === 0) {
      setProcessedData({
        entries: [],
        hourlyData: {},
        dailyData: {},
        weeklyData: {},
        moodCounts: {},
        sentimentData: { positive: 0, neutral: 0, negative: 0 },
        averageSentimentScore: 0,
        totalEntries: 0,
        dateLabel: 'No Data'
      });
      return;
    }

    switch (range) {
      case 'daily':
        processDailyData(data);
        break;
      case 'weekly':
        processWeeklyData(data);
        break;
      case 'monthly':
        processMonthlyData(data);
        break;
      default:
        processDailyData(data);
    }
  };

  const processDailyData = (data) => {
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    const dayEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= startOfDay && entryDate <= endOfDay;
    });

    // Group by 2-hour intervals
    const hourlyData = {};
    for (let hour = 0; hour < 24; hour += 2) {
      hourlyData[hour] = [];
    }

    dayEntries.forEach(entry => {
      const hour = new Date(entry.timestamp).getHours();
      const interval = Math.floor(hour / 2) * 2;
      hourlyData[interval].push(entry);
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    dayEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: dayEntries,
      hourlyData,
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: dayEntries.length,
      dateLabel: startOfDay.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    });
  };

  const processWeeklyData = (data) => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const weekEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= weekStart && entryDate <= weekEnd;
    });

    // Group by day
    const dailyData = {};
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    days.forEach(day => {
      dailyData[day] = [];
    });

    weekEntries.forEach(entry => {
      const dayName = days[new Date(entry.timestamp).getDay()];
      dailyData[dayName].push(entry);
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    weekEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: weekEntries,
      dailyData,
      hourlyData: {},
      weeklyData: {},
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: weekEntries.length,
      dateLabel: `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`
    });
  };

  const processMonthlyData = (data) => {
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    const monthEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= monthStart && entryDate <= monthEnd;
    });

    // Group by week
    const weeklyData = {};
    for (let i = 1; i <= 5; i++) {
      weeklyData[`Week ${i}`] = [];
    }

    monthEntries.forEach(entry => {
      const date = new Date(entry.timestamp);
      const weekNum = Math.ceil(date.getDate() / 7);
      if (weeklyData[`Week ${weekNum}`]) {
        weeklyData[`Week ${weekNum}`].push(entry);
      }
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    monthEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: monthEntries,
      weeklyData,
      hourlyData: {},
      dailyData: {},
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: monthEntries.length,
      dateLabel: monthStart.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    });
  };

  const handlePreviousDate = () => {
    const newDate = new Date(selectedDate);
    if (timeRange === 'daily') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (timeRange === 'weekly') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setSelectedDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = new Date(selectedDate);
    const today = new Date();
    
    if (timeRange === 'daily') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (timeRange === 'weekly') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    
    if (newDate > today) {
      return;
    }
    
    setSelectedDate(newDate);
  };

  const getMostFrequentMoods = () => {
    if (!processedData || !processedData.moodCounts) return [];
    
    return Object.entries(processedData.moodCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([mood, count]) => ({
        mood,
        count,
        percentage: Math.round((count / processedData.totalEntries) * 100)
      }));
  };

  const getRiskLevelBadge = (riskLevel) => {
    const colors = {
      POSITIVE: '#4CAF50',
      LOW: '#8BC34A',
      MILD: '#FFC107',
      MODERATE: '#FF9800',
      HIGH: '#F44336',
      CRISIS: '#D32F2F',
      UNKNOWN: '#9E9E9E'
    };

    const color = colors[riskLevel] || colors.UNKNOWN;
    
    return (
      <span 
        className="risk-badge"
        style={{ 
          backgroundColor: color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      >
        {riskLevel}
      </span>
    );
  };

  const handleRerunAnalysis = async (entryId) => {
    if (!entryId) return;
    
    setRerunningAnalysis(prev => ({ ...prev, [entryId]: true }));
    
    try {
      console.log('🔄 Re-running AI analysis for entry:', entryId);
      
      const response = await moodAnalyticsService.rerunSentimentAnalysis(entryId);
      
      if (response.success) {
        console.log('✅ AI analysis re-run successfully');
        await fetchPatientMoodData();
      } else {
        console.error('❌ Failed to re-run AI analysis');
        alert('Failed to re-run analysis. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error re-running AI analysis:', error);
      alert('Error re-running analysis. Please try again.');
    } finally {
      setRerunningAnalysis(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const getSentimentChartData = () => {
    if (!processedData) return null;

    return {
      labels: ['Positive', 'Neutral', 'Negative'],
      datasets: [{
        data: [
          processedData.sentimentData.positive,
          processedData.sentimentData.neutral,
          processedData.sentimentData.negative
        ],
        backgroundColor: ['#4CAF50', '#FFC107', '#F44336'],
        borderColor: ['#45a049', '#FFB300', '#E53935'],
        borderWidth: 2
      }]
    };
  };

  const getMoodTrendChartData = () => {
    if (!processedData) return null;

    let labels = [];
    let data = [];

    if (timeRange === 'daily') {
      if (!processedData.hourlyData) return null;
      for (let hour = 0; hour < 24; hour += 2) {
        labels.push(`${hour}:00`);
        const entries = processedData.hourlyData[hour] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      }
    } else if (timeRange === 'weekly') {
      if (!processedData.dailyData) return null;
      labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      labels.forEach(day => {
        const fullDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][labels.indexOf(day)];
        const entries = processedData.dailyData[fullDay] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      });
    } else if (timeRange === 'monthly') {
      if (!processedData.weeklyData) return null;
      labels = Object.keys(processedData.weeklyData);
      labels.forEach(week => {
        const entries = processedData.weeklyData[week] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      });
    }

    return {
      labels,
      datasets: [{
        label: 'Average Mood Rating',
        data,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        spanGaps: true
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            const labels = ['', 'Upset', 'Struggling', 'Okay', 'Good', 'Great'];
            return labels[value] || '';
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((context.parsed / total) * 100);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  const spinnerStyle = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  if (!patientId) {
    return (
      <div className="mood-analysis-page">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="error-state">
          <p>Patient ID is not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mood-analysis-page">
    <style>{spinnerStyle}</style>
    
    {/* New styled header */}
    <div className="mood-analysis-header">
      <div className="mood-header-container">
        <button className="mood-back-button" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        
        <div className="mood-patient-details">
          <h1 className="mood-patient-name">{patientData?.patientName || 'Patient'}</h1>
          <p className="mood-patient-email">{patientData?.patientEmail || 'No email available'}</p>
        </div>
        
        <div className="mood-header-right">
          <span className="mood-active-badge">
            <span className="mood-status-dot"></span>
            Active
          </span>
          <span className="mood-reg-date">
            Registration Date: {patientData?.registrationDate 
              ? new Date(patientData.registrationDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric', 
                  year: 'numeric'
                })
              : 'April 12, 2025'
            }
          </span>
        </div>
      </div>
    </div>

      <div className="page-controls">
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
            {recentEntries.length > 0 && (
              <span className="tab-badge">
                {recentEntries.length}
              </span>
            )}
          </button>
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button 
            className={`tab-button ${activeTab === 'recommendations' ? 'active' : ''}`}
            onClick={() => setActiveTab('recommendations')}
          >
            Recommendations
          </button>
        </div>
      </div>

      <div className="page-content">
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading mood data...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="error-state">
            <p>Error: {error}</p>
            <button onClick={fetchPatientMoodData}>Retry</button>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {activeTab === 'recent' && (
              <div className="recent-tab">
                <h3>Recent Mood Check-Ins (Last 6 Hours)</h3>
                
                {recentEntries.length > 0 ? (
                  <div className="recent-entries-list">
                    {recentEntries.map((entry, index) => (
                      <div key={entry._id || index} className="recent-entry-card">
                        <div className="entry-header">
                          <div className="entry-mood-info">
                              <img 
                                src={getMoodEmoji(entry.moodKey)} 
                                alt={entry.moodLabel}
                                className="mood-emoji"
                                style={{ width: '32px', height: '32px' }}
                              />
                              <div>
                                <div className="mood-label">{entry.moodLabel}</div>
                                <div className="entry-time">
                                {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })} - {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </div>
                            </div>
                          </div>
                          
                          <button
                            className="rerun-ai-button"
                            onClick={() => handleRerunAnalysis(entry._id)}
                            disabled={rerunningAnalysis[entry._id]}
                          >
                            {rerunningAnalysis[entry._id] ? (
                              <>
                                <span className="spinner">⟳</span>
                                Re-running...
                              </>
                            ) : (
                              <>
                                <span>🔄</span>
                                Re-run AI Analysis
                              </>
                            )}
                          </button>
                        </div>

                        {entry.reflection && (
                          <div className="reflection-box">
                            <div className="reflection-label">Reflection:</div>
                            {entry.reflection}
                          </div>
                        )}

                        {entry.sentiment && (
                          <div className="sentiment-analysis-section">
                            <div className="sentiment-header">AI Sentiment Analysis:</div>
                            
                            <div className="sentiment-grid">
                              <div className="sentiment-item">
                                <span className="sentiment-label">Sentiment Type:</span>
                                <span className={`sentiment-type ${entry.sentiment.type}`}>
                                  {entry.sentiment.type}
                                </span>
                              </div>
                              
                              <div className="sentiment-item">
                                <span className="sentiment-label">Confidence Score:</span>
                                <span className="sentiment-value">{entry.sentiment.score}%</span>
                              </div>
                              
                              <div className="sentiment-item">
                                <span className="sentiment-label">Risk Level:</span>
                                {getRiskLevelBadge(entry.sentiment.riskLevel)}
                              </div>
                              
                              {entry.sentiment.phq9Analysis && (
                                <div className="sentiment-item">
                                  <span className="sentiment-label">PHQ-9 Symptoms:</span>
                                  <span className="sentiment-value">
                                    {entry.sentiment.phq9Analysis.symptomCount} detected
                                  </span>
                                </div>
                              )}
                              
                              {entry.sentiment.moodValidation && (
                                <div className="sentiment-item">
                                  <span className="sentiment-label">Mood Validation:</span>
                                  <span className={`mood-validation ${entry.sentiment.moodValidation.status}`}>
                                    {entry.sentiment.moodValidation.status}
                                  </span>
                                </div>
                              )}
                              
                              {entry.sentiment.clinicalNotes && (
                                <div className="clinical-notes">
                                  <div className="notes-label">Clinical Notes:</div>
                                  <div className="notes-content">
                                    {entry.sentiment.clinicalNotes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">🕐</div>
                    <p className="empty-title">No recent mood check-ins in the last 6 hours</p>
                    <p className="empty-subtitle">Recent entries will appear here as patients log their moods</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'overview' && processedData && (
              <div className="overview-tab">
                <div className="overview-controls">
                  <div className="time-range-selector">
                    <label>View:</label>
                    <select 
                      value={timeRange} 
                      onChange={(e) => {
                        setError(null);
                        setTimeRange(e.target.value);
                      }}
                      className="time-range-dropdown"
                      disabled={isLoading}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  <div className="date-selector">
                    <button 
                      className="nav-button"
                      onClick={handlePreviousDate}
                      disabled={isLoading}
                    >
                      ←
                    </button>
                    <span className="date-display">
                      {processedData?.dateLabel || selectedDate.toLocaleDateString()}
                    </span>
                    <button 
                      className="nav-button"
                      onClick={handleNextDate}
                      disabled={isLoading || selectedDate >= new Date()}
                    >
                      →
                    </button>
                  </div>
                </div>

                {processedData.entries.length > 0 ? (
                  <div className="analytics-content">
                    {/* Summary Cards */}
                    <div className="summary-section">
                      <h3>Sentiment Summary: {processedData.dateLabel}</h3>
                      <div className="summary-cards">
                        <div className="summary-card positive">
                          <div className="card-label">Positive</div>
                          <div className="card-value">{processedData.sentimentData.positive}</div>
                          <div className="card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="summary-card neutral">
                          <div className="card-label">Neutral</div>
                          <div className="card-value">{processedData.sentimentData.neutral}</div>
                          <div className="card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="summary-card negative">
                          <div className="card-label">Negative</div>
                          <div className="card-value">{processedData.sentimentData.negative}</div>
                          <div className="card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="summary-card unlogged">
                          <div className="card-label">Unlogged</div>
                          <div className="card-value">
                            {timeRange === 'daily' 
                              ? Math.max(0, 12 - processedData.totalEntries)
                              : 0
                            }
                          </div>
                          <div className="card-detail">/ {timeRange === 'daily' ? '12 check-ins' : 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Overall Score and Summary */}
                    <div className="score-summary-section">
                      <div className="score-card">
                        <h4>Overall Sentiment Score</h4>
                        <div className="score-value">{processedData.averageSentimentScore}%</div>
                        <div className="score-summary">
                          {processedData.averageSentimentScore >= 70 ? (
                            <p>{patientData?.patientName} has been feeling mostly positive during this period.</p>
                          ) : processedData.averageSentimentScore >= 40 ? (
                            <p>{patientData?.patientName} has had mixed emotions during this period.</p>
                          ) : (
                            <p>{patientData?.patientName} has been struggling emotionally during this period.</p>
                          )}
                        </div>
                      </div>

                      <div className="mood-frequency-card">
                        <h4>Most Frequent Moods</h4>
                        <div className="mood-list">
                          {getMostFrequentMoods().map((item, index) => (
                            <div key={index} className="mood-item">
                              <img 
                                src={getMoodEmoji(item.mood)} 
                                alt={item.mood}
                                className="mood-emoji"
                                style={{ width: '24px', height: '24px' }}
                              />
                              <span className="mood-name">{item.mood}</span>
                              <span className="mood-count">{item.count} times ({item.percentage}%)</span>
                            </div>
                          ))}
                        </div>

                      </div>
                    </div>

                    {/* Charts Section */}
                    <div className="charts-section">
                      <div className="chart-container">
                        <h4>Mood Trend Over Time</h4>
                        <div className="chart-wrapper">
                          {getMoodTrendChartData() ? (
                            <Line 
                              data={getMoodTrendChartData()} 
                              options={chartOptions} 
                            />
                          ) : (
                            <div className="no-chart-data">
                              <p>No mood data available for this time period</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="chart-container">
                        <h4>Sentiment Distribution</h4>
                        <div className="chart-wrapper">
                          {getSentimentChartData() ? (
                            <Doughnut 
                              data={getSentimentChartData()} 
                              options={doughnutOptions} 
                            />
                          ) : (
                            <div className="no-chart-data">
                              <p>No sentiment data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Clinical Summary */}
                    {clinicalSummary && (
                      <div className="clinical-summary-section">
                        <h4>Clinical Summary</h4>
                        <div className="clinical-summary-content">
                          <div className="summary-item">
                            <span className="summary-label">Risk Assessment:</span>
                            <span className="summary-value">{clinicalSummary.riskLevel || 'Not Available'}</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Trend Analysis:</span>
                            <span className="summary-value">{clinicalSummary.trendAnalysis || 'Not Available'}</span>
                          </div>
                          {clinicalSummary.recommendations && (
                            <div className="summary-item">
                              <span className="summary-label">Recommendations:</span>
                              <span className="summary-value">{clinicalSummary.recommendations}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <p className="empty-title">No mood data for {processedData.dateLabel}</p>
                    <p className="empty-subtitle">Try selecting a different date or time range</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-tab">
                <h3>Mood Check-In History</h3>
                
                {moodHistory.length > 0 ? (
                  <div className="history-list">
                    {moodHistory.map((entry, index) => (
                      <div key={entry._id || index} className="history-entry-card">
                        <div className="history-entry-header">
                         <div className="entry-mood-display">
                          <img 
                            src={getMoodEmoji(entry.moodKey)} 
                            alt={entry.moodLabel}
                            className="mood-emoji"
                            style={{ width: '32px', height: '32px' }}
                          />
                          <div className="mood-details">
                            <div className="mood-label">{entry.moodLabel}</div>
                            <div className="mood-rating">Rating: {entry.moodRating}/5</div>
                          </div>
                        </div>
                          <div className="entry-timestamp">
                            {new Date(entry.timestamp).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        </div>

                        {entry.reflection && (
                          <div className="history-reflection">
                            <div className="reflection-label">Patient Reflection:</div>
                            <div className="reflection-text">{entry.reflection}</div>
                          </div>
                        )}

                        {entry.sentiment && (
                          <div className="history-sentiment">
                            <div className="sentiment-summary">
                              <span className={`sentiment-badge ${entry.sentiment.type}`}>
                                {entry.sentiment.type}
                              </span>
                              <span className="sentiment-score">{entry.sentiment.score}% confidence</span>
                              {getRiskLevelBadge(entry.sentiment.riskLevel)}
                            </div>
                            
                            {entry.sentiment.clinicalNotes && (
                              <div className="clinical-notes-preview">
                                <strong>Clinical Notes:</strong> {entry.sentiment.clinicalNotes}
                              </div>
                            )}
                          </div>
                        )}

                        <button
                          className="rerun-history-ai-button"
                          onClick={() => handleRerunAnalysis(entry._id)}
                          disabled={rerunningAnalysis[entry._id]}
                        >
                          {rerunningAnalysis[entry._id] ? (
                            <>
                              <span className="spinner">⟳</span>
                              Re-running...
                            </>
                          ) : (
                            <>
                              🔄 Re-analyze
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">📝</div>
                    <p className="empty-title">No mood check-in history available</p>
                    <p className="empty-subtitle">Patient mood entries will appear here once they start logging</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recommendations' && (
              <div className="recommendations-tab">
                <h3>Clinical Recommendations</h3>
                
                <div className="recommendations-content">
                  <div className="recommendation-section">
                    <h4>📋 Assessment Summary</h4>
                    <div className="assessment-grid">
                      <div className="assessment-item">
                        <span className="assessment-label">Total Check-ins:</span>
                        <span className="assessment-value">{moodHistory.length}</span>
                      </div>
                      <div className="assessment-item">
                        <span className="assessment-label">Average Sentiment:</span>
                        <span className="assessment-value">
                          {processedData?.averageSentimentScore || 0}%
                        </span>
                      </div>
                      <div className="assessment-item">
                        <span className="assessment-label">Dominant Mood:</span>
                        <span className="assessment-value">
                          {getMostFrequentMoods()[0]?.mood || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="recommendation-section">
                    <h4>🎯 Treatment Recommendations</h4>
                    <div className="recommendation-list">
                      {processedData?.averageSentimentScore >= 70 ? (
                        <>
                          <div className="recommendation-item positive">
                            <div className="recommendation-icon">✅</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Maintain Current Progress</div>
                              <div className="recommendation-description">
                                Patient shows consistent positive mood patterns. Continue current treatment approach.
                              </div>
                            </div>
                          </div>
                          <div className="recommendation-item">
                            <div className="recommendation-icon">🔄</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Regular Check-ins</div>
                              <div className="recommendation-description">
                                Schedule monthly follow-ups to monitor continued progress.
                              </div>
                            </div>
                          </div>
                        </>
                      ) : processedData?.averageSentimentScore >= 40 ? (
                        <>
                          <div className="recommendation-item warning">
                            <div className="recommendation-icon">⚠️</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Monitor Closely</div>
                              <div className="recommendation-description">
                                Mixed emotional patterns detected. Consider increasing session frequency.
                              </div>
                            </div>
                          </div>
                          <div className="recommendation-item">
                            <div className="recommendation-icon">🧠</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Cognitive Behavioral Therapy</div>
                              <div className="recommendation-description">
                                CBT techniques may help address mood fluctuations and negative thought patterns.
                              </div>
                            </div>
                          </div>
                          <div className="recommendation-item">
                            <div className="recommendation-icon">💊</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Medication Review</div>
                              <div className="recommendation-description">
                                Consider reviewing current medication effectiveness with prescribing physician.
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="recommendation-item urgent">
                            <div className="recommendation-icon">🚨</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Immediate Attention Required</div>
                              <div className="recommendation-description">
                                Concerning mood patterns detected. Schedule urgent assessment.
                              </div>
                            </div>
                          </div>
                          <div className="recommendation-item">
                            <div className="recommendation-icon">📞</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Crisis Intervention Protocol</div>
                              <div className="recommendation-description">
                                Implement safety planning and consider intensive outpatient support.
                              </div>
                            </div>
                          </div>
                          <div className="recommendation-item">
                            <div className="recommendation-icon">👥</div>
                            <div className="recommendation-content">
                              <div className="recommendation-title">Support System Activation</div>
                              <div className="recommendation-description">
                                Engage patient's support network and emergency contacts if necessary.
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="recommendation-section">
                    <h4>📈 Monitoring Suggestions</h4>
                    <div className="monitoring-list">
                      <div className="monitoring-item">
                        <span className="monitoring-icon">📱</span>
                        <span className="monitoring-text">Encourage daily mood check-ins</span>
                      </div>
                      <div className="monitoring-item">
                        <span className="monitoring-icon">📊</span>
                        <span className="monitoring-text">Review sentiment trends weekly</span>
                      </div>
                      <div className="monitoring-item">
                        <span className="monitoring-icon">🔄</span>
                        <span className="monitoring-text">Re-run AI analysis for concerning entries</span>
                      </div>
                      <div className="monitoring-item">
                        <span className="monitoring-icon">📋</span>
                        <span className="monitoring-text">Document significant mood pattern changes</span>
                      </div>
                    </div>
                  </div>

                  <div className="recommendation-section">
                    <h4>⚠️ Red Flags to Monitor</h4>
                    <div className="red-flags-list">
                      <div className="red-flag-item">
                        <span className="flag-icon">🔴</span>
                        <span className="flag-text">Sudden drop in mood ratings</span>
                      </div>
                      <div className="red-flag-item">
                        <span className="flag-icon">🔴</span>
                        <span className="flag-text">Mentions of self-harm or suicidal ideation</span>
                      </div>
                      <div className="red-flag-item">
                        <span className="flag-icon">🔴</span>
                        <span className="flag-text">Significant decrease in check-in frequency</span>
                      </div>
                      <div className="red-flag-item">
                        <span className="flag-icon">🔴</span>
                        <span className="flag-text">Persistent high-risk sentiment classifications</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MoodAnalysisPage;